import { useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { CheckCircle2 } from "lucide-react";
import { heroContent } from "../../../data/homepage";
import PrimaryButton from "../../ui/PrimaryButton";
import SecondaryButton from "../../ui/SecondaryButton";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80";

function HeroSection() {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const visualRef = useRef(null);

  const [lineOne, lineTwo] = useMemo(() => {
    const [first, second] = heroContent.title.split(", ");
    return [first || heroContent.title, second || "with confidence and clarity."];
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
      );
      gsap.fromTo(
        visualRef.current,
        { opacity: 0, y: 40, x: 20 },
        { opacity: 1, y: 0, x: 0, duration: 0.9, delay: 0.15, ease: "power3.out" }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white pb-20 pt-40 sm:pt-44 lg:min-h-[850px] lg:pt-48"
    >
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(196,198,207,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(196,198,207,0.2) 1px, transparent 1px)",
            backgroundSize: "88px 88px"
          }}
        />
      </div>
      <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div ref={contentRef} style={{ opacity: 0 }}>
            <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">
              <span className="h-px w-10 bg-brand-gold-strong/70" />
              {heroContent.eyebrow}
            </p>

            <h1 className="mt-7 text-4xl font-semibold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl lg:text-[5.1rem]">
              {lineOne}
              <span className="mt-1 block italic text-brand-navy/78">{lineTwo}</span>
            </h1>

            <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-charcoal/75 sm:text-lg">
              {heroContent.description}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <PrimaryButton showArrow to={heroContent.primaryCta.to}>
                {heroContent.primaryCta.label}
              </PrimaryButton>
              <SecondaryButton to={heroContent.secondaryCta.to}>
                {heroContent.secondaryCta.label}
              </SecondaryButton>
            </div>

            <ul className="mt-9 space-y-2.5">
              {heroContent.heroHighlights.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-brand-charcoal/75">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-strong" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div ref={visualRef} className="relative mx-auto w-full max-w-md lg:mx-0" style={{ opacity: 0 }}>
            <div className="absolute -left-6 -top-6 h-full w-full bg-brand-navy/10" />
            <article className="relative overflow-hidden border-r-[7px] border-b-[7px] border-brand-gold-strong bg-white shadow-[0_34px_60px_-30px_rgba(10,31,68,0.45)]">
              <img
                src={HERO_IMAGE}
                alt="Students discussing admissions plans"
                className="h-[460px] w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-deep/82 via-brand-navy-deep/15 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-soft">
                  Student + Parent Focus
                </p>
                <p className="mt-2 text-xl font-semibold leading-tight">{heroContent.trustLine}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
